/**
 * Seed-content shapes and the build-time shape check (TASKS T37, BRIEF <content>).
 *
 * > Each item: `id, tier, es, en, modes, tags, vetted, verdict, source`.
 *
 * A seed item is **not** an `ItemRecord`. The record adds `memory` and `failureCount`, which are
 * facts about one learner's history and cannot exist in a file checked into the repository. The
 * loader mints those at intake time from the injected clock — see `toItemRecord`. Keeping the two
 * shapes separate is what stops a scheduler state from ever being authored by hand.
 *
 * Pure: no I/O, no DOM, no clock. The corpus arrives as already-parsed JSON.
 */

import type { Person, Tense } from '../lang/conjugation';
import type { ModeId, Tier } from '../state/gamestate';
// Explicit `.ts`, for the same reason as `lang/variety.ts`: this module is imported directly by
// `scripts/check-content.ts` under `node --experimental-strip-types`, whose resolver does no
// extensionless resolution. The two type-only imports above are erased and so need no extension.
// See DECISIONS D-070.
import { newMemory, type ItemRecord, type ItemSource, type Verdict } from '../store/schema.ts';

/** Bumped when a seed-file shape changes. Distinct from the persisted `SCHEMA_VERSION`. */
export const CONTENT_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------------------------

/**
 * A carrier sentence for a single-word item.
 *
 * Tier 2 is "top ~300 words" (SPEC §5) but the modes are cloze and tiles, and neither can present a
 * bare word: cloze needs a sentence to cut a hole in, tiles need a sentence to shuffle. So a tier-2
 * item carries the word in `es` — that is what is graded — and the sentence it lives in here. See
 * DECISIONS D-072.
 */
export interface Example {
  es: string;
  en: string;
}

/**
 * The conjugate-into-slot spec for a tier-3 or tier-6 item.
 *
 * `es` holds the expected surface form; this holds what the learner is *shown*. Storing both is
 * redundant on purpose: the redundancy is checked at build time against `lang/conjugation.ts`, so a
 * mis-typed verb form in a JSON file fails the build instead of silently teaching a wrong form.
 */
export interface Slot {
  infinitive: string;
  tense: Tense;
  person: Person;
}

export interface SeedItem {
  id: string;
  tier: Tier;
  /** The production target — what the learner must produce, and what grading compares against. */
  es: string;
  en: string;
  modes: ModeId[];
  tags: string[];
  /**
   * The Diana gate. ALWAYS `false` in a seed file, regardless of any LLM verdict: machine vetting
   * is a filter, not authority (BRIEF <spanish_correctness>). Typed as the literal `false` so a
   * file claiming `true` is a type error as well as a build-check failure.
   */
  vetted: false;
  /** Written by `scripts/vet-content.ts` (T42). `null` until that has run. */
  verdict: Verdict | null;
  source: ItemSource;
  example?: Example;
  slot?: Slot;
  /** For tier 4: the `ConversionRule.id` this item exercises. */
  rule?: string;
  /**
   * Set when this item is a **hazard**: a word its rule does not reach (T52).
   *
   * Never authored in a JSON file — `hazardItems` mints these from each rule's `hazards` list at
   * load time. It is what lets the conversión card show the rule while asking for a word the rule
   * would get wrong, which is the whole mechanism against over-application.
   */
  hazard?: { trap: string; note: string };
  /**
   * How this form is *derived* from something the learner already has — shown once, at first
   * exposure, and never again.
   *
   * The distinction from `en` is the whole point. `en` is the gloss: what the word means. This is
   * the reason the word is what it is: `situation` ends `-tion`, so it ends `-ción`. `hablar` is an
   * `-ar` verb, so its subjunctive borrows the `-er` endings. A learner who has the derivation does
   * not need the item again, because he can regenerate it — and can generate the several hundred
   * words like it that nobody will ever put on a card.
   *
   * Optional, and deliberately absent on most items. A derivation invented for a word that simply
   * has to be learned would be worse than none: it would teach a rule that is not there.
   */
  derivation?: string;
}

/** One conversion rule plus its hazards (BRIEF <conversion_engine>). */
export interface ConversionRule {
  id: string;
  /**
   * `ending` is a productive suffix rule — the thing that yields hundreds of words for free.
   * `false-friends` is a drill set with no generative rule behind it.
   *
   * The distinction is explicit rather than inferred because the two behave differently at draw
   * time: an ending rule unlocks and then generates, while a false-friend set only ever interleaves
   * against over-application. Collapsing them would have meant giving the false friends a fake
   * `from`/`to` pair, and the reveal card would then claim a rule was "generating words for free"
   * when it generates nothing. See DECISIONS D-075.
   */
  kind: 'ending' | 'false-friends';
  /** The English ending, as shown to the learner. Descriptive for a `false-friends` set. */
  from: string;
  /** The Spanish ending it becomes. */
  to: string;
  name: string;
  /** Grammatical gender the ending forces, where it forces one. */
  gender: 'f' | 'm' | null;
  note: string;
  /** Worked examples. These are the tier-4 items the rule generates. */
  examples: SeedItem[];
  /**
   * High-frequency exceptions and false friends, interleaved deliberately once the rule unlocks so
   * the rule is never over-applied (BRIEF <conversion_engine>).
   */
  hazards: Hazard[];
}

export interface Hazard {
  id: string;
  /** The English word that *looks* like it should take the rule. */
  en: string;
  /** What it actually is in Spanish. */
  es: string;
  /** What the rule would wrongly produce, or what English speakers wrongly assume it means. */
  trap: string;
  note: string;
}

/** A constructive pattern: Spanish assembled from parts rather than memorised whole. */
export interface ConversionPattern {
  id: string;
  name: string;
  /** The shape, in the notation shown to the learner (e.g. `estar + -ando/-iendo`). */
  shape: string;
  note: string;
  examples: SeedItem[];
}

export interface RuleFile {
  rules: ConversionRule[];
}

export interface PatternFile {
  patterns: ConversionPattern[];
}

// ---------------------------------------------------------------------------------------------
// Legal modes per tier
// ---------------------------------------------------------------------------------------------

/**
 * Which modes each tier may use (SPEC §5).
 *
 * Checked at build time because a tier-2 item that lists `speech` would vanish from every device
 * without a microphone — the failure would look like missing content, not like a typo, and would be
 * found by nobody. DoD-4 requires tiers 2-6 to remain fully playable with speech unavailable.
 */
export const TIER_MODES: Readonly<Record<Tier, readonly ModeId[]>> = {
  1: ['speech'],
  2: ['cloze', 'tiles'],
  3: ['conjugate'],
  4: ['conversion'],
  5: ['tiles', 'sentence', 'speech'],
  6: ['conjugate', 'sentence'],
};

const VALID_MODES: ReadonlySet<string> = new Set<ModeId>([
  'tiles',
  'cloze',
  'conjugate',
  'conversion',
  'sentence',
  'speech',
  'encoding',
]);

const VALID_SOURCES: ReadonlySet<string> = new Set<ItemSource>([
  'seed',
  'conversation-log',
  'conversion-rule',
]);

const VALID_TENSES: ReadonlySet<string> = new Set<Tense>([
  'present',
  'preterite',
  'imperfect',
  'subjunctive',
  'conditional',
]);

const VALID_PERSONS: ReadonlySet<string> = new Set<Person>([
  'yo',
  'tu',
  'el',
  'nosotros',
  'ustedes',
]);

/** Lowercase, digits, hyphens. Ids appear in exported CSV, so no commas, quotes, or spaces. */
const ID_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// ---------------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------------

export interface ShapeError {
  /** The item id where known, else a positional locator. */
  where: string;
  field: string;
  message: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

/**
 * Every shape problem with one seed item. Returns all of them, not the first: fixing a corpus one
 * error per run is the sort of loop that gets a check disabled.
 */
export function validateSeedItem(value: unknown, locator: string): ShapeError[] {
  const errors: ShapeError[] = [];
  const at = (field: string, message: string): void => {
    errors.push({ where: locator, field, message });
  };

  if (!isPlainObject(value)) {
    return [{ where: locator, field: '(root)', message: 'item is not an object' }];
  }

  const id = value['id'];
  if (typeof id !== 'string' || id.length === 0) at('id', 'missing or not a string');
  else if (!ID_RE.test(id)) at('id', `"${id}" must be lowercase kebab-case`);

  const where = typeof id === 'string' && id.length > 0 ? id : locator;
  const say = (field: string, message: string): void => {
    errors.push({ where, field, message });
  };

  const tier = value['tier'];
  if (typeof tier !== 'number' || !Number.isInteger(tier) || tier < 1 || tier > 6) {
    say('tier', `must be an integer 1-6, got ${JSON.stringify(tier)}`);
  }

  for (const field of ['es', 'en'] as const) {
    const text = value[field];
    if (typeof text !== 'string' || text.trim().length === 0) {
      say(field, 'missing or empty');
    } else if (text !== text.trim()) {
      say(field, 'has leading or trailing whitespace');
    }
  }

  const modes = value['modes'];
  if (!Array.isArray(modes) || modes.length === 0) {
    say('modes', 'must be a non-empty array');
  } else if (!isStringArray(modes)) {
    say('modes', 'must be an array of strings');
  } else {
    for (const mode of modes) {
      if (!VALID_MODES.has(mode)) say('modes', `unknown mode "${mode}"`);
    }
    if (new Set(modes).size !== modes.length) say('modes', 'contains duplicates');
    if (typeof tier === 'number' && tier >= 1 && tier <= 6) {
      const legal = TIER_MODES[tier as Tier];
      for (const mode of modes) {
        if (VALID_MODES.has(mode) && !legal.includes(mode as ModeId)) {
          say('modes', `mode "${mode}" is not legal for tier ${tier} (SPEC §5)`);
        }
      }
    }
  }

  if (!isStringArray(value['tags'])) say('tags', 'must be an array of strings');

  // The Diana gate. Not a warning: an item shipping pre-vetted defeats the whole layer.
  if (value['vetted'] !== false) {
    say('vetted', 'must be exactly false in a seed file — machine vetting is not authority');
  }

  const verdict = value['verdict'];
  if (verdict !== null && verdict !== undefined) {
    errors.push(...validateVerdict(verdict, where));
  } else if (verdict === undefined) {
    say('verdict', 'missing — use null when the item has not been vetted');
  }

  const source = value['source'];
  if (typeof source !== 'string' || !VALID_SOURCES.has(source)) {
    say('source', `must be one of ${[...VALID_SOURCES].join(' | ')}`);
  }

  // Optional shapes, and the tier rules that make them mandatory.
  const example = value['example'];
  if (example !== undefined) {
    if (!isPlainObject(example)) say('example', 'must be an object');
    else {
      for (const field of ['es', 'en'] as const) {
        const text = example[field];
        if (typeof text !== 'string' || text.trim().length === 0) {
          say(`example.${field}`, 'missing or empty');
        }
      }
      if (typeof example['es'] === 'string' && typeof value['es'] === 'string') {
        if (!containsWord(example['es'], value['es'])) {
          say('example.es', `does not contain the target "${String(value['es'])}"`);
        }
      }
    }
  }

  const slot = value['slot'];
  if (slot !== undefined) {
    if (!isPlainObject(slot)) say('slot', 'must be an object');
    else {
      const infinitive = slot['infinitive'];
      if (typeof infinitive !== 'string' || !/(ar|er|ir)$/.test(infinitive)) {
        say('slot.infinitive', 'must be an -ar / -er / -ir infinitive');
      }
      if (typeof slot['tense'] !== 'string' || !VALID_TENSES.has(slot['tense'])) {
        say('slot.tense', `must be one of ${[...VALID_TENSES].join(' | ')}`);
      }
      if (typeof slot['person'] !== 'string' || !VALID_PERSONS.has(slot['person'])) {
        say('slot.person', `must be one of ${[...VALID_PERSONS].join(' | ')}`);
      }
    }
  }

  const derivation = value['derivation'];
  if (derivation !== undefined && (typeof derivation !== 'string' || derivation.trim().length === 0)) {
    say('derivation', 'must be a non-empty string when present');
  }

  if (typeof tier === 'number') {
    if ((tier === 3 || tier === 6) && slot === undefined && hasMode(modes, 'conjugate')) {
      say('slot', `a tier-${tier} conjugate item needs a slot`);
    }
    /*
     * Tier 6 is the tense ladder, and it is the tier where a derivation matters most: the preterite
     * and the subjunctive are large regular systems that a learner either derives or memorises one
     * form at a time. Requiring it here is what keeps a tense item from shipping as a flashcard.
     */
    if (tier === 6 && derivation === undefined) {
      say('derivation', 'a tier-6 item must say how the form is derived, not only what it means');
    }
    if (tier === 2 && example === undefined) {
      say('example', 'a tier-2 item needs a carrier sentence for cloze and tiles');
    }
    if (tier === 4 && typeof value['rule'] !== 'string') {
      say('rule', 'a tier-4 item must name the conversion rule it exercises');
    }
  }

  return errors;
}

function hasMode(modes: unknown, mode: ModeId): boolean {
  return isStringArray(modes) && modes.includes(mode);
}

/**
 * Whole-word containment, accent- and case-sensitive.
 *
 * Deliberately strict: the point of the check is to catch a carrier sentence that was edited while
 * the target was not, and a loose match would let `come` pass inside `comemos`.
 */
function containsWord(sentence: string, target: string): boolean {
  const words = sentence.toLowerCase().match(/[\p{L}\p{M}]+/gu) ?? [];
  const parts = target.toLowerCase().match(/[\p{L}\p{M}]+/gu) ?? [];
  if (parts.length === 0) return false;
  // Multi-word targets ("hay que") must appear as a contiguous run.
  for (let i = 0; i + parts.length <= words.length; i += 1) {
    if (parts.every((part, j) => words[i + j] === part)) return true;
  }
  return false;
}

export function validateVerdict(value: unknown, where: string): ShapeError[] {
  if (!isPlainObject(value)) {
    return [{ where, field: 'verdict', message: 'must be an object or null' }];
  }
  const errors: ShapeError[] = [];
  for (const field of ['model', 'register'] as const) {
    if (typeof value[field] !== 'string' || (value[field] as string).length === 0) {
      errors.push({ where, field: `verdict.${field}`, message: 'missing or not a string' });
    }
  }
  const date = value['date'];
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(date)) {
    errors.push({ where, field: 'verdict.date', message: 'must be an ISO-8601 date' });
  }
  for (const field of ['grammatical', 'natural', 'mexican'] as const) {
    if (typeof value[field] !== 'boolean') {
      errors.push({ where, field: `verdict.${field}`, message: 'must be a boolean' });
    }
  }
  if (value['correction'] !== undefined && typeof value['correction'] !== 'string') {
    errors.push({ where, field: 'verdict.correction', message: 'must be a string when present' });
  }
  return errors;
}

// ---------------------------------------------------------------------------------------------
// Quarantine
// ---------------------------------------------------------------------------------------------

/**
 * Verdicts written by `scripts/vet-content.ts`, keyed by item id.
 *
 * Kept beside the corpus rather than inside it so that machine-written data never mixes with
 * hand-authored Spanish in the same file (DECISIONS D-078).
 */
export interface VerdictFile {
  model: string | null;
  /** ISO-8601 timestamp of the vetting run. */
  generatedAt: string | null;
  verdicts: Record<string, Verdict>;
}

/**
 * Does this verdict quarantine its item? (BRIEF <spanish_correctness> layer 1.)
 *
 * > Items failing vetting are quarantined and never served.
 *
 * An unvetted item — `null` — is NOT quarantined. Quarantine is a judgement that was made and came
 * back bad; treating "not yet checked" as "failed" would empty the corpus the first time this ran
 * on a fresh clone, and would make the absence of the vetting step indistinguishable from its
 * failure. The brief's separate guarantee, that a missing API key fails loudly, is what stops an
 * unvetted corpus from shipping unnoticed.
 */
export function isQuarantined(verdict: Verdict | null | undefined): boolean {
  if (!verdict) return false;
  return (
    !verdict.grammatical || !verdict.natural || !verdict.mexican || verdict.register === 'vulgar'
  );
}

// ---------------------------------------------------------------------------------------------
// Corpus level
// ---------------------------------------------------------------------------------------------

export interface CorpusReport {
  ok: boolean;
  itemsChecked: number;
  errors: ShapeError[];
}

/**
 * Shape-check a whole corpus, including cross-item rules that a per-item check cannot see.
 *
 * Duplicate ids are the one that matters: the store is keyed by id, so two items sharing one would
 * silently share a scheduler memory and each would corrupt the other's interval.
 */
export function validateCorpus(items: readonly unknown[], file = 'corpus'): CorpusReport {
  const errors: ShapeError[] = [];
  const seen = new Map<string, number>();

  items.forEach((value, index) => {
    errors.push(...validateSeedItem(value, `${file}[${index}]`));
    if (isPlainObject(value) && typeof value['id'] === 'string') {
      const id = value['id'];
      const first = seen.get(id);
      if (first !== undefined) {
        errors.push({
          where: id,
          field: 'id',
          message: `duplicate id, already used at index ${first}`,
        });
      } else {
        seen.set(id, index);
      }
    }
  });

  return { ok: errors.length === 0, itemsChecked: items.length, errors };
}

/** One line per error, for the build log. */
export function formatShapeReport(report: CorpusReport, label = 'content'): string {
  if (report.ok) {
    return `${label} shape check: ${report.itemsChecked} items checked, all well-formed.`;
  }
  const lines = [
    `${label} shape check FAILED: ${report.errors.length} problems across ${report.itemsChecked} items.`,
    '',
  ];
  for (const error of report.errors) {
    lines.push(`  ${error.where}: ${error.field} — ${error.message}`);
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------------------------

export class ContentShapeError extends Error {
  // A plain field, not a constructor parameter property: `node --experimental-strip-types` is
  // strip-only and rejects parameter properties, and this module is loaded that way by
  // `scripts/check-content.ts`.
  readonly report: CorpusReport;

  constructor(report: CorpusReport) {
    super(formatShapeReport(report));
    this.name = 'ContentShapeError';
    this.report = report;
  }
}

/**
 * Validate and narrow. Throws on any problem — there is no partial load.
 *
 * A loader that dropped bad items and carried on would turn a typo into a silently smaller corpus,
 * and the learner would never know which words the game decided not to teach him.
 */
export function loadSeedItems(items: readonly unknown[], file = 'corpus'): SeedItem[] {
  const report = validateCorpus(items, file);
  if (!report.ok) throw new ContentShapeError(report);
  return items as SeedItem[];
}

/**
 * Mint the runtime record for a seed item at intake.
 *
 * `dueAt` comes from the injected clock at the moment the item is introduced, never from the file:
 * a due date authored into content would mean every learner inherits the author's calendar.
 */
export function toItemRecord(seed: SeedItem, dueAt: number): ItemRecord {
  return {
    id: seed.id,
    tier: seed.tier,
    es: seed.es,
    en: seed.en,
    modes: [...seed.modes],
    tags: [...seed.tags],
    vetted: false,
    verdict: seed.verdict,
    source: seed.source,
    memory: newMemory(dueAt),
    failureCount: 0,
  };
}

/** Every seed item a rule file contributes: the worked examples, which are tier-4 items. */
export function itemsFromRules(file: RuleFile): SeedItem[] {
  return file.rules.flatMap((rule) => rule.examples);
}

/**
 * The hazards, as tier-4 items in their own right (T52, BRIEF <conversion_engine>).
 *
 * > Once a rule is unlocked, deliberately interleave hazards so the rule is never over-applied.
 *
 * Interleaving something the round builder cannot see is not interleaving, so a hazard has to be a
 * real item with its own scheduler memory: `question → pregunta`, presented *under* the `-tion →
 * -ción` rule, is a retrieval the learner either survives or does not, and it has to come back on
 * its own schedule when he does not.
 *
 * The id prefix does the sequencing. Intake orders by tier and then by id, so every `conv-*` example
 * of every rule is offered before any `hz-*` hazard of it — which is the "once a rule is unlocked"
 * half of the brief's sentence, and it costs no extra machinery. Asserted in
 * `tests/unit/conversion.spec.ts` rather than left to be true by accident.
 *
 * A hazard whose answer is already taught by some rule's worked example is skipped: two items with
 * the same prompt and the same answer are two FSRS memories for one fact, and the learner would be
 * asked it twice as often as anything else. See DECISIONS D-128.
 */
export function hazardItems(file: RuleFile): SeedItem[] {
  const taught = new Set(
    file.rules.flatMap((rule) => rule.examples.map((example) => example.es.toLowerCase())),
  );
  return file.rules.flatMap((rule) =>
    rule.hazards
      .filter((hazard) => !taught.has(hazard.es.toLowerCase()))
      .map(
        (hazard): SeedItem => ({
          id: hazard.id,
          tier: 4,
          es: hazard.es,
          en: hazard.en,
          modes: ['conversion'],
          tags: ['conversion', rule.id, 'hazard'],
          vetted: false,
          verdict: null,
          source: 'conversion-rule',
          rule: rule.id,
          hazard: { trap: hazard.trap, note: hazard.note },
        }),
      ),
  );
}

export function itemsFromPatterns(file: PatternFile): SeedItem[] {
  return file.patterns.flatMap((pattern) => pattern.examples);
}
